import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Header, Page, Content } from '@backstage/core-components';
import BarChartIcon from '@material-ui/icons/BarChart';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

interface AnalyticsSummary {
  sessions: { last7d: number; last30d: number };
  commits: { last30d: number };
  topTools: Array<{ tool: string; calls: number }>;
  topPersonas: Array<{ personaId: string; sessions: number }>;
}

function StatCard({ value, label }: { value: number | string; label: string }) {
  const classes = useStyles();
  return (
    <Card>
      <CardContent className={classes.statCard}>
        <Typography className={classes.statValue}>{value}</Typography>
        <Typography variant="body2" className={classes.statLabel}>{label}</Typography>
      </CardContent>
    </Card>
  );
}

export function CopilotAdminPage() {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const backendUrl = configApi.getOptionalString('backend.baseUrl') ?? '';

  useEffect(() => {
    const githubToken = localStorage.getItem('githubToken') ?? '';
    fetch(`${backendUrl}/api/copilot/analytics/summary`, {
      headers: githubToken ? { 'x-github-token': githubToken } : {},
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<AnalyticsSummary>;
      })
      .then(data => {
        setSummary(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, [backendUrl]);

  return (
    <Page themeId="tool">
      <Header
        title="Copilot Analytics"
        subtitle="Usage statistics for the platform Copilot integration"
        type="admin"
        typeLink="/copilot-admin"
      />
      <Content>
        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box mt={2}>
            <Typography color="error">Failed to load analytics: {error}</Typography>
          </Box>
        )}

        {summary && (
          <>
            {/* ── Top stats ── */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <StatCard value={summary.sessions.last7d} label="Sessions (last 7 days)" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard value={summary.sessions.last30d} label="Sessions (last 30 days)" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard value={summary.commits.last30d} label="Copilot-assisted commits (30 days)" />
              </Grid>
            </Grid>

            {/* ── Top personas ── */}
            <Typography variant="h6" className={classes.sectionTitle}>
              <BarChartIcon style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Top Personas (30 days)
            </Typography>
            <Card>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Persona</TableCell>
                    <TableCell align="right">Sessions</TableCell>
                    <TableCell align="right">Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.topPersonas.map(p => (
                    <TableRow key={p.personaId} className={classes.tableRow}>
                      <TableCell>
                        <Chip
                          label={p.personaId}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{p.sessions}</TableCell>
                      <TableCell align="right">
                        {summary.sessions.last30d > 0
                          ? `${Math.round((p.sessions / summary.sessions.last30d) * 100)}%`
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {summary.topPersonas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">No data yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>

            {/* ── Top tools ── */}
            <Typography variant="h6" className={classes.sectionTitle}>
              Top Tools Invoked (30 days)
            </Typography>
            <Card>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tool</TableCell>
                    <TableCell align="right">Invocations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.topTools.map(t => (
                    <TableRow key={t.tool} className={classes.tableRow}>
                      <TableCell>
                        <code>{t.tool}</code>
                      </TableCell>
                      <TableCell align="right">{t.calls}</TableCell>
                    </TableRow>
                  ))}
                  {summary.topTools.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">No tool calls recorded yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>

            <Box mt={3}>
              <Typography variant="caption" color="textSecondary">
                Data sourced from the local PostgreSQL copilot_events table. For deeper analysis,
                use Application Insights KQL queries — see{' '}
                <code>docs/copilot-analytics-kql.md</code> in the platform repo.
              </Typography>
            </Box>
          </>
        )}
      </Content>
    </Page>
  );
}
